import { InAppMessageView } from "../view/InAppMessageView"
import {
  InAppMessageActionInteraction,
  InAppMessageCloseInteraction,
  InAppMessageImageImpressionInteraction,
  InAppMessageInteraction
} from "./InAppMessageInteraction"
import { InAppMessageViewEventHandleProcessor } from "./InAppMessageViewEventHandleProcessor"
import { InAppMessageAction } from "../../../../core/internal/model/model"
import { InAppMessageActionViewEvent, InAppMessageViewEvent } from "./InAppMessageViewEvent"

export interface InAppMessageInteractionHandler<Interaction extends InAppMessageInteraction> {
  supports(interaction: InAppMessageInteraction): boolean

  handle(view: InAppMessageView, interaction: Interaction): void
}

export class InAppMessageInteractionHandlerFactory {
  constructor(private readonly handlers: InAppMessageInteractionHandler<InAppMessageInteraction>[]) {}

  get(interaction: InAppMessageInteraction): InAppMessageInteractionHandler<InAppMessageInteraction> | undefined {
    return this.handlers.find((it) => it.supports(interaction))
  }
}

export class InAppMessageCloseInteractionHandler
  implements InAppMessageInteractionHandler<InAppMessageCloseInteraction>
{
  handle(view: InAppMessageView, interaction: InAppMessageCloseInteraction): void {
    view.close()
  }

  supports(interaction: InAppMessageInteraction): boolean {
    return interaction.type === "CLOSE"
  }
}

export class InAppMessageActionInteractionHandler
  implements InAppMessageInteractionHandler<InAppMessageActionInteraction>
{
  constructor(private readonly handleProcessor: InAppMessageViewEventHandleProcessor) {}

  handle(view: InAppMessageView, interaction: InAppMessageActionInteraction): void {
    const event = this.eventOrNull(view, interaction)
    if (!event) return
    this.handleProcessor.process(view, event, ["TRACK", "ACTION"])
  }

  private eventOrNull(
    view: InAppMessageView,
    interaction: InAppMessageActionInteraction
  ): InAppMessageActionViewEvent | null {
    const action = InAppMessageAction.fromJson(interaction.action)
    if (!action) return null
    return InAppMessageViewEvent.action({
      timestamp: view.ui.clock.currentMillis(),
      action,
      area: interaction.area,
      buttonText: interaction.buttonText,
      imageUrl: interaction.imageUrl,
      imageOrder: interaction.imageOrder
    })
  }

  supports(interaction: InAppMessageInteraction): boolean {
    return interaction.type === "ACTION"
  }
}

export class InAppMessageImageImpressionInteractionHandler
  implements InAppMessageInteractionHandler<InAppMessageImageImpressionInteraction>
{
  constructor(private readonly handleProcessor: InAppMessageViewEventHandleProcessor) {}

  handle(view: InAppMessageView, interaction: InAppMessageImageImpressionInteraction): void {
    const event = InAppMessageViewEvent.imageImpression({
      timestamp: view.ui.clock.currentMillis(),
      imageUrl: interaction.imageUrl,
      imageOrder: interaction.imageOrder
    })
    this.handleProcessor.process(view, event, ["TRACK", "ACTION"])
  }

  supports(interaction: InAppMessageInteraction): boolean {
    return interaction.type === "IMAGE_IMPRESSION"
  }
}
